import cloneDeep from 'lodash/cloneDeep';
import helperFunctions from '../utils/helperFunctions';
import { provideService } from '../services/provider';
import { CreditDataSourceCA, CreditBureaus } from '../utils/enums';
import creditBureaus from '@/utils/enums/creditBureaus';

const initialState = {
  questionData: {},
  answerData: {},
  creditScore: {
    creditGrade: null, // Need to make this nullable in the backend
    creditGradeText: '',
    creditVisionScore: null,
    creditEquifaxScore: null, // this is only to be sent to the backend, not to be displayed in the UI
  },
  tradeLines: [],
  hasEquifaxTradeLines: false,
  equifaxTradeLines: [],
  regionalCreditScore: null,
  hasCreditPlusCreditResults: false,
  hasRegionalCreditResults: false,
  hasEquifaxCreditResults: false,
  bureauCode: null,
  preQualificationLoans: [],
  selectedPreQualificationLoans: [],
  matchedInventoryResults: null,
  selectedEquifaxTradeLine: {
    tradeLineId: null,
    balanceOnTrade: 0,
  },
  // If user has tried to check their credit and it failed (both transunion and regional failed)
  // used in ecom only for now
  creditChecksFailed: null,
  // used for CIM credit flow
  CIMFinanceApplicationVehicle: null,
  applyCIMFinanceApplicationVehicle: false,
};

const mutations = {
  SET_STATE(state, [key, data]) {
    state[key] = data;
  },
  SET_QUESTION_DATA(state, value) {
    state.questionData = value;
  },
  SET_QUESTION_ANSWERS(state, value) {
    state.answerData = value;
  },
  SET_CREDIT_GRADE(state, value) {
    state.creditScore.creditGrade = value;
  },
  SET_BUREAU_CODE(state, value) {
    state.bureauCode = value;
  },
  SET_REGIONAL_CREDIT(state, value) {
    state.regionalCreditScore = value;
    state.hasRegionalCreditResults = true;
  },
  SET_CREDIT_PLUS_DATA(state, value) {
    state.creditScore.creditGrade = value.scoreLetter;
    state.creditScore.creditVisionScore = parseInt(value.score, 10);
    state.tradeLines = value.tradeLines;
    state.hasCreditPlusCreditResults = true;
  },
  SET_EQUIFAX_CREDIT_DATA(state, value) {
    state.creditScore.creditGrade = value.scoreLetter;
    state.creditScore.creditEquifaxScore = parseInt(value.score, 10);
    state.hasEquifaxCreditResults = true;
    state.hasEquifaxTradeLines = value.tradeLines;
  },
  SET_PRE_QUALIFICATION_LOANS(state, value) {
    state.preQualificationLoans = value;
  },
  SET_PRE_QUALIFICATION_LOAN_APPLIED(state) {
    state.preQualificationLoans = state.preQualificationLoans.map(prequalification => {
      if (state.selectedPreQualificationLoans.includes(prequalification.providerKey)) {
        // eslint-disable-next-line no-param-reassign
        prequalification.applied = true;
      }
      return prequalification; // before update lead action
    });
  },
  SET_SELECTED_PRE_QUALIFICATION_LOAN(state, lenderKey) {
    state.selectedPreQualificationLoans.push(lenderKey);
  },
  SET_SELECTED_EQUIFAX_TRADE_LINE(state, tradeline) {
    state.selectedEquifaxTradeLine.tradeLineId = tradeline.id;
    state.selectedEquifaxTradeLine.balanceOnTrade = tradeline.balanceOnTrade;
  },
};
const actions = {
  async getQuestions({ rootState }) {
    // TODO: refactoring this
    const { user, lead } = rootState;
    const payload = {
      firstName: user.firstName,
      lastName: user.lastName,
      birthdate: helperFunctions.birthdateShaper(user.birthdate),
      phoneNumber: user.phoneNumber,
      address: {
        addressLine1: user.streetAddress,
        addressLine2: user.unit,
        city: user.city,
        postalCode: user.postalCode,
        countryCode: user.country,
        province: user.provinceCode,
      },
      language: user.language,
      reportId: lead.leadId,
      apiKey: rootState.business.api.key,
      externalId: rootState.business.api.externalID,
    };

    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    let product;
    if (rootState.business.info.creditSettings.defaultCreditDataSourceCA === CreditDataSourceCA.NEIGHBOURHOOD_CREDIT) {
      product = 'regional-credit';
    } else {
      product = 'credit';
    }
    const creditService = provideService({
      product,
      isDemo,
    });

    return creditService.getQuestions(payload);
  },
  setQuestions({ commit }, payload) {
    const metaData = {};
    metaData.started = payload.started.attributes.value;
    metaData.matchKeyId = payload.matchKeyId.attributes.value;
    metaData.applicationId = payload.applicationId.attributes.value;
    metaData.equifaxIds = payload.equifaxIds ?? null;

    const creditCheckData = Object.values(payload).reduce(
      (acc, current) => {
        if (current.name.match(/answer_/)) {
          // Set a pre-select answers for test user info
          if (current.attributes.value) {
            acc.userAnswers.push(current.attributes.value);
          }
          acc.questions.push(current.attributes.label);
          acc.choices.push(Object.keys(current.attributes.choices));
          acc.choicesObj.push(current.attributes.choices);
        }

        if (current.name.match(/meta_/)) {
          acc.metas.push(current.attributes.value);
        }

        if (current.name.match(/options_/)) {
          acc.optionKeys.push(current.attributes.value);
        }
        return acc;
      },
      {
        // starting object for reduce
        questions: [],
        metas: [],
        optionKeys: [],
        choices: [],
        choicesObj: [],
        userAnswers: [],
      }
    );

    commit('SET_QUESTION_DATA', {
      ...metaData,
      ...creditCheckData,
    });
  },
  async getCreditScore({ rootState, state, commit, dispatch }, data) {
    commit('SET_QUESTION_ANSWERS', data);

    const { user, lead } = rootState;

    const answerPayload = {};
    answerPayload.started = state.questionData.started;
    answerPayload.reportId = lead.leadId;
    answerPayload.applicationId = state.questionData.applicationId;
    answerPayload.matchKeyId = state.questionData.matchKeyId;
    answerPayload.applicationLanguage = user.language;
    answerPayload.apiKey = rootState.business.api.key;
    answerPayload.externalId = rootState.business.api.externalID;

    const answersArray = state.answerData;

    if (
      rootState.business.info.creditSettings.defaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX ||
      rootState.business.info.creditSettings.defaultCreditDataSourceCA ===
        CreditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT
    ) {
      answerPayload.equifaxIds = state.questionData.equifaxIds;

      state.answerData.forEach((answer, index) => {
        answersArray[index] = state.questionData.choicesObj[index][answer];
      });
    }

    for (let i = 0; i < state.questionData.questions.length; i += 1) {
      answerPayload[`meta_${i}`] = state.questionData.metas[i];
      answerPayload[`answer_${i}`] = answersArray[i];
      answerPayload[`options_${i}`] = state.questionData.optionKeys[i];
    }

    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    let product;
    let res;
    if (
      rootState.business.info.creditSettings.prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled &&
      rootState.business.info.creditSettings.defaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX
    ) {
      product = 'equifax-enhanced-credit';
    } else if (rootState.business.info.creditSettings.defaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX) {
      product = 'equifax-credit';
    } else {
      product = 'credit';
    }
    const creditService = provideService({
      product,
      isDemo,
    });

    try {
      res = await creditService.answerQuestions(answerPayload);
      if (
        rootState.business.info.creditSettings.defaultCreditDataSourceCA ===
        CreditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT
      ) {
        // We need to display regional credit score in this scenario.
        // answerQuestions request lets backend know the user is ID authenticated or not.
        res = await dispatch('getRegionalCreditScore');
        return res;
      }
      if (res.data.bureauCode === CreditBureaus.EFX_PERSONALIZED) {
        commit('SET_EQUIFAX_CREDIT_DATA', res.data);
      } else if (res.data.bureauCode === CreditBureaus.TU) {
        // This means it is credit plus
        commit('SET_CREDIT_PLUS_DATA', res.data);
      }

      commit('SET_BUREAU_CODE', res.data.bureauCode);

      return res;
    } catch (error) {
      res = await dispatch('getRegionalCreditScore');
      this._vm.$log('Error in getCreditScore', error);

      return res;
    }
  },
  async getRegionalCreditScore({ rootState, commit }) {
    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    const creditService = provideService({
      product: 'regional-credit',
      isDemo,
    });

    const res = await creditService.getRegionalCreditScore(rootState.user.postalCode);
    commit('SET_REGIONAL_CREDIT', res.data.letter);
    // If the credit data source is EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT, we want to set the bureauCode to this new value instead of regional
    if (
      rootState.business.info.creditSettings.defaultCreditDataSourceCA ===
      CreditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT
    ) {
      commit('SET_BUREAU_CODE', creditBureaus.EFX_ID_AUTH_NEIGHBOURHOOD_CREDIT);
    } else {
      commit('SET_BUREAU_CODE', res.data.bureauCode);
    }
    return res;
  },
  async getPreQualificationLoans({ state, rootState, commit }) {
    const payload = {
      creditScore: state.creditScore.creditVisionScore || state.creditScore.creditEquifaxScore,
      leadId: rootState.lead.leadId,
      vehicleCondition: rootState.route.query.condition || null,
      incomeAndDebt: {
        monthlyDebtExpenses: rootState.user.financeDetails.monthlyExpenses,
        grossIncome: {
          annualIncome: rootState.user.financeDetails.annualIncome,
          monthlyIncome: rootState.user.financeDetails.monthlyIncome,
          hourlyWage: rootState.user.financeDetails.hourlyWage,
          hoursPerWeek: rootState.user.financeDetails.hoursPerWeek,
        },
        monthlyHousingCosts: rootState.user.financeDetails.monthlyPayment,
      },
    };

    const creditService = provideService({
      product: 'credit',
    });

    if (!rootState.business.info.creditSettings.prequalificationEnabled || !payload.creditScore) {
      return;
    }

    try {
      const res = await creditService.calculatePrequalifications(
        rootState.business.api.key,
        rootState.business.api.externalID,
        payload
      );
      commit('SET_PRE_QUALIFICATION_LOANS', res.data);
    } catch (e) {
      throw new Error('get prequalification loans failed');
    }
  },
  async getMatchedInventory({ rootState, commit }) {
    if (rootState.business.info.creditSettings.prequalificationConsumerInventoryMatchingEnabled) {
      const creditService = provideService({
        product: 'credit',
        isDemo: false,
      });
      // const res = require('@/mock/inventoryMatchedResults2.json');
      // commit('SET_STATE', ['matchedInventoryResults', res]);
      const res = await creditService.getConsumerInventoryMatch(rootState.lead.leadId);
      commit('SET_STATE', ['matchedInventoryResults', res.data]);
    }
  },

  async getTradeLines({ rootState, commit }) {
    const isDemo = process.env.VUE_APP_APP_ENV === 'test' || rootState.business.info.isDemo;
    let product;
    if (
      rootState.business.info.creditSettings.prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled &&
      rootState.business.info.creditSettings.defaultCreditDataSourceCA === CreditDataSourceCA.EQUIFAX
    ) {
      product = 'equifax-enhanced-credit';
    } else {
      product = 'credit';
    }
    try {
      const creditService = provideService({
        product,
        isDemo,
      });
      const res = await creditService.getTradeLines(rootState.lead.leadId);
      commit('SET_STATE', ['equifaxTradeLines', res.data]);
    } catch (error) {
      this._vm.$log('Error in get trade lines: ', error);
    }
  },
};
const getters = {
  creditScoreGrade: state => state.creditScore.creditGrade,
  creditVisionScore: state => state.creditScore.creditVisionScore,
  regionalCreditScoreGrade: state => state.regionalCreditScore,
  hasCreditPlusCreditResults: state => state.hasCreditPlusCreditResults,
  hasRegionalCreditResults: state => state.hasCreditPlusCreditResults,
  hasEquifaxCreditResults: state => state.hasEquifaxCreditResults,
  hasEquifaxTradeLines: state => state.hasEquifaxTradeLines,
  getEquifaxTradeLines: state => state.equifaxTradeLines,
  preQualificationResult: state => state.preQualificationLoans[0],
  preQualificationEnabled(state, localGetters, rootState) {
    return rootState.business.info.creditSettings.prequalificationEnabled;
  },
  preQualificationLoansExist(state, localGetters, rootState) {
    return rootState.business.info.creditSettings.prequalificationEnabled && state.preQualificationLoans.length > 0;
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  mutations,
  actions,
  getters,
};
